import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { fetchStorefront } from '../../services/storefrontService';
import StorefrontMaintenancePage from '../../pages/StorefrontMaintenancePage';
import { DEFAULT_STOREFRONT_SETTINGS } from '../../utils/storefrontThemes';
import StorefrontBootSplash from './StorefrontBootSplash';

const CRAWLER_UA = /googlebot|bingbot|yandex|duckduckbot|baiduspider|facebookexternalhit|twitterbot|slurp|crawler|spider/i;

const StorefrontMaintenanceGate = () => {
  const { companyCode: routeCompanyCode } = useParams();
  const resolvedCompanyCode = useMemo(
    () => String(routeCompanyCode || process.env.REACT_APP_PUBLIC_COMPANY_CODE || '').trim() || null,
    [routeCompanyCode]
  );

  const [loading, setLoading] = useState(true);
  const [bootReady, setBootReady] = useState(false);
  const [company, setCompany] = useState(null);
  const [storefrontSettings, setStorefrontSettings] = useState(DEFAULT_STOREFRONT_SETTINGS);
  const [storefrontPayload, setStorefrontPayload] = useState(null);
  const bootStartedAt = useMemo(() => Date.now(), []);

  const loadStorefront = useCallback(async () => {
    if (!resolvedCompanyCode) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await fetchStorefront(resolvedCompanyCode);
      setCompany(data.company || null);
      setStorefrontSettings(data.storefrontSettings || DEFAULT_STOREFRONT_SETTINGS);
      setStorefrontPayload(data);
    } catch (error) {
      console.error('Maintenance gate load error:', error);
    } finally {
      setLoading(false);
    }
  }, [resolvedCompanyCode]);

  useEffect(() => {
    loadStorefront();
  }, [loadStorefront]);

  useEffect(() => {
    if (loading) {
      setBootReady(false);
      return undefined;
    }
    if (CRAWLER_UA.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
      setBootReady(true);
      return undefined;
    }
    const remaining = Math.max(0, 350 - (Date.now() - bootStartedAt));
    const timer = setTimeout(() => setBootReady(true), remaining);
    return () => clearTimeout(timer);
  }, [loading, bootStartedAt]);

  if (loading || !bootReady) {
    return <StorefrontBootSplash company={company} />;
  }

  if (storefrontSettings?.maintenanceMode) {
    return (
      <StorefrontMaintenancePage
        company={company}
        storefrontSettings={storefrontSettings}
      />
    );
  }

  return <Outlet context={{ company, storefrontSettings, storefrontPayload }} />;
};

export default StorefrontMaintenanceGate;
